import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/layout/Layout";
import { listParts, createPart, updatePart } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, AlertTriangle, Boxes, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function Parts() {
  const { user } = useAuth();
  const [parts, setParts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => setParts(await listParts(q));
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q]);

  const lowStock = useMemo(
    () => parts.filter((p) => p.stock <= p.reorder_at).length,
    [parts]
  );

  const isAdmin = user?.role === "admin";

  return (
    <Layout
      title="Parts & Inventory"
      breadcrumbs={["Operations", "Inventory"]}
      actions={
        isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-part-btn"
                className="bg-orange-500 hover:bg-orange-600 rounded-sm font-semibold"
              >
                <Plus className="h-4 w-4 mr-1" /> Part
              </Button>
            </DialogTrigger>
            <PartDialog
              part={null}
              onSaved={() => {
                setOpen(false);
                load();
              }}
            />
          </Dialog>
        )
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Kpi label="SKUs" value={parts.length} icon={Boxes} accent="bg-blue-50 text-blue-700" />
        <Kpi
          label="Low Stock"
          value={lowStock}
          icon={AlertTriangle}
          accent={lowStock > 0 ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}
        />
        <Kpi
          label="Inventory Value"
          value={`$${parts
            .reduce((a, p) => a + (p.unit_cost || 0) * (p.stock || 0), 0)
            .toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          icon={Boxes}
          accent="bg-orange-50 text-orange-700"
        />
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
        <Input
          data-testid="part-search"
          placeholder="Search by SKU or name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9 h-10 rounded-sm bg-white border-zinc-300"
        />
      </div>

      <div className="engineered-card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              <Th>SKU</Th>
              <Th>Name</Th>
              <Th>Category</Th>
              <Th>Stock</Th>
              <Th>Reorder At</Th>
              <Th>Unit Cost</Th>
              <Th>Location</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {parts.map((p) => {
              const low = p.stock <= p.reorder_at;
              return (
                <tr
                  key={p.id}
                  data-testid={`part-row-${p.sku}`}
                  className="border-b border-zinc-100 hover:bg-zinc-50"
                >
                  <td className="px-4 py-3 font-mono font-bold">{p.sku}</td>
                  <td className="px-4 py-3">{p.name}</td>
                  <td className="px-4 py-3 text-xs text-zinc-600 uppercase tracking-wider">
                    {p.category}
                  </td>
                  <td className="px-4 py-3 font-mono">
                    <span
                      className={`px-2 py-0.5 rounded-sm text-xs font-bold ${
                        low
                          ? "bg-red-50 text-red-700 border border-red-300"
                          : "bg-emerald-50 text-emerald-700 border border-emerald-300"
                      }`}
                    >
                      {p.stock} {p.unit}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-600">{p.reorder_at}</td>
                  <td className="px-4 py-3 font-mono">${p.unit_cost?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-xs text-zinc-600">{p.location}</td>
                  <td className="px-4 py-3">
                    {user?.role === "admin" && (
                      <Dialog
                        open={editing === p.id}
                        onOpenChange={(o) => setEditing(o ? p.id : null)}
                      >
                        <DialogTrigger asChild>
                          <button
                            data-testid={`edit-part-${p.sku}`}
                            className="text-zinc-500 hover:text-orange-600"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        </DialogTrigger>
                        <PartDialog
                          part={p}
                          onSaved={() => {
                            setEditing(null);
                            load();
                          }}
                        />
                      </Dialog>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {parts.length === 0 && (
          <div className="p-8 text-center text-sm text-zinc-500">No parts.</div>
        )}
      </div>
    </Layout>
  );
}

const Th = ({ children }) => (
  <th className="px-4 py-2 text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-500">
    {children}
  </th>
);

const Kpi = ({ label, value, icon: Icon, accent }) => (
  <div className="engineered-card p-4 flex items-center gap-3">
    <div className={`h-10 w-10 rounded-sm flex items-center justify-center ${accent}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
        {label}
      </div>
      <div className="font-mono font-extrabold text-2xl text-zinc-950">{value}</div>
    </div>
  </div>
);

function PartDialog({ part, onSaved }) {
  const [form, setForm] = useState(
    part || {
      sku: "",
      name: "",
      category: "HVAC",
      unit: "ea",
      unit_cost: 0,
      stock: 0,
      reorder_at: 0,
      location: "",
    }
  );
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.sku || !form.name) {
      toast.error("SKU and name required");
      return;
    }
    setBusy(true);
    try {
      if (part) {
        await updatePart(part.id, {
          name: form.name,
          category: form.category,
          unit: form.unit,
          unit_cost: Number(form.unit_cost),
          stock: Number(form.stock),
          reorder_at: Number(form.reorder_at),
          location: form.location,
        });
      } else {
        await createPart({
          ...form,
          unit_cost: Number(form.unit_cost),
          stock: Number(form.stock),
          reorder_at: Number(form.reorder_at),
        });
      }
      toast.success(part ? "Part updated" : "Part created");
      onSaved();
    } catch {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="rounded-sm">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">
          {part ? "Edit Part" : "New Part"}
        </DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-4">
        <F label="SKU" testid="part-sku" disabled={!!part}
           value={form.sku} onChange={(v) => setForm({ ...form, sku: v })} />
        <F label="Name" testid="part-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="part-category" className="rounded-sm h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="HVAC">HVAC</SelectItem>
              <SelectItem value="Equipment">Equipment</SelectItem>
              <SelectItem value="Civil">Civil</SelectItem>
              <SelectItem value="General">General</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <F label="Unit" testid="part-unit" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} />
        <F label="Unit Cost" testid="part-cost" type="number" value={form.unit_cost}
           onChange={(v) => setForm({ ...form, unit_cost: v })} />
        <F label="Stock" testid="part-stock" type="number" value={form.stock}
           onChange={(v) => setForm({ ...form, stock: v })} />
        <F label="Reorder At" testid="part-reorder" type="number" value={form.reorder_at}
           onChange={(v) => setForm({ ...form, reorder_at: v })} />
        <F label="Location" testid="part-location" value={form.location || ""}
           onChange={(v) => setForm({ ...form, location: v })} />
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={busy} data-testid="part-submit"
                className="bg-orange-500 hover:bg-orange-600 rounded-sm font-bold">
          {busy ? "Saving…" : part ? "Save" : "Create"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

const F = ({ label, value, onChange, testid, type = "text", disabled }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider">{label}</Label>
    <Input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="rounded-sm h-11"
    />
  </div>
);
